import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetUserProfile } from 'Features/userProfile/api/getUserProfile'
import { useUpdateUserProfile } from 'Features/userProfile/api/updateUserProfile'
import { Timezones } from 'Assets/lists/timezones'

import {
  FieldsWrapper,
  UserProfileSection,
} from 'Features/userProfile/routes/UserProfile/UserProfile.styled'
import { ProfileSectionTitle } from '../ProfileSection/ProfileSection.styled'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { shallow } from 'zustand/shallow'
import { GeneralSelect } from '@clickcease/cc-client-components-lib'
import { SelectOption } from 'Types/SelectOption'
import { capitalizeFirstLetter } from 'Utils/stringHelper'
import { LanguagesTypes } from 'Types/Languages'
import { useSetLanguage } from 'Hooks/useSetLanguage'
import languages from 'Assets/lists/languages'
import dateFormats from 'Assets/lists/dateFormats'

export const PreferencesSection = () => {
  const { t } = useTranslation('userProfile')
  const [timezone, setTimezone] = useState<SelectOption>()
  const [language, setLanguage] = useState<SelectOption>()
  const [dateFormat, setDateFormat] = useState<SelectOption>()
  const { setLanguage: setUserLanguage } = useSetLanguage()
  const [theme, setTheme] = useState<SelectOption>()
  const { mutate } = useUpdateUserProfile()
  const userQuery = useGetUserProfile()
  const { showToaster } = useAppStore(
    (state) => ({
      showToaster: state.showToaster,
    }),
    shallow
  )

  const userData = useMemo(() => {
    if (!(userQuery.data instanceof Error)) {
      return userQuery.data
    }
  }, [userQuery])

  const setDateFormatValue = () => {
    const dateFormat = dateFormats.find((format) => format.value === userData?.locale)
    setDateFormat(dateFormat)
  }

  useEffect(() => {
    if (userData) {
      setTheme({ value: userData.theme, label: userData.theme })
      setDateFormatValue()
      setLanguage(languages.find((languageOption) => languageOption.value === userData.language))
      setTimezone(
        Timezones.find((timezoneOption) => timezoneOption.value === userData.timezoneCountry)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData])

  const mutateOptions = {
    onSuccess: () => showToaster(t('successfully_updated'), 'success'),
    onError: () => showToaster(t('updating_error'), 'failure'),
  }

  const themesOptions = [
    {
      value: 'light',
      label: t('light'),
    },
    {
      value: 'dark',
      label: t('dark'),
    },
  ]

  const onLanguagechangeSuccess = async (language: LanguagesTypes) => {
    showToaster(t('successfully_updated'), 'success')
    await setUserLanguage(language)
  }

  const selectStyle = { width: '250px' }

  return (
    <UserProfileSection id={'user-profile-preferences-section'}>
      <ProfileSectionTitle>{t('preferences')}</ProfileSectionTitle>
      <FieldsWrapper>
        <GeneralSelect
          value={timezone}
          options={Timezones}
          title={t('timezone')}
          topTitle
          wrapperCSS={selectStyle}
          onChange={(item: SelectOption) => {
            setTimezone(item)
            if (item?.value) mutate({ type: 'timezoneCountry', value: item.value }, mutateOptions)
          }}
        />
        <GeneralSelect
          value={language}
          options={languages}
          title={t('language')}
          topTitle
          wrapperCSS={selectStyle}
          onChange={(item: SelectOption) => {
            setLanguage(item)
            if (item?.value)
              mutate(
                { type: 'language', value: item.value },
                {
                  onSuccess: async () => {
                    await onLanguagechangeSuccess(item.value as LanguagesTypes)
                  },
                }
              )
          }}
        />

        <GeneralSelect
          value={dateFormat}
          options={dateFormats}
          title={t('dateFormat')}
          topTitle
          wrapperCSS={selectStyle}
          onChange={(item: SelectOption) => {
            setDateFormat(item)
            if (item?.value) mutate({ type: 'locale', value: item.value }, mutateOptions)
          }}
        />

        {false && ( // TODO: remove when theme is ready
          <GeneralSelect
            value={theme}
            options={themesOptions}
            title={t('theme')}
            topTitle
            wrapperCSS={selectStyle}
            onChange={(item: SelectOption) => {
              setTheme(item)
              if (item?.value)
                mutate(
                  {
                    type: 'theme',
                    value: capitalizeFirstLetter(item.value),
                  },
                  mutateOptions
                )
            }}
          />
        )}
      </FieldsWrapper>
    </UserProfileSection>
  )
}
